import { usePropertyField } from './usePropertyField';
import { IApplicationNonRental } from '@apply/api/src/api/ApplyApi';

type NonRentalFormField = 'budget' | 'fixFlipBudget' | 'landBudget' | 'specSheet';

/**
 * Reads and writes the `savedFormId` for a specific form type on `property.nonRental`.
 *
 * Wraps `usePropertyField` with the specific nested updater for
 * `information.properties.items[idx].nonRental[field].savedFormId`.
 *
 * @param propertyGuid - GUID of the property
 * @param field        - which nonRental form field: 'budget' | 'fixFlipBudget' | 'landBudget' | 'specSheet'
 * @returns            [currentSavedFormId, setter]
 *
 * @example
 * const [, setSavedFormId] = useNonRentalSavedFormId(propertyGuid, 'budget');
 * // After API save:
 * if (savedFormResponse?.result) setSavedFormId(savedFormResponse.result);
 */
export function useNonRentalSavedFormId(
    propertyGuid: string,
    field: NonRentalFormField
): [number | undefined, (id: number) => void] {
    return usePropertyField(
        propertyGuid,
        (p) => p.nonRental?.[field]?.savedFormId,
        (p, savedFormId) => ({
            ...p,
            nonRental: {
                ...(p.nonRental ?? {}),
                [field]: {
                    ...(p.nonRental?.[field] ?? {}),
                    savedFormId,
                },
            } as IApplicationNonRental,
        })
    );
}
